import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import styles from './ui-placement.css';
import {
    UIPlacementAlignments,
    UIPlacementDirections,
} from './ui-placement.type';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIPlacement
 * @element ui-placement
 * @classdesc Represents a class for <code>ui-placement</code> element.
 * This element is used to place elements on the line with alignment to left,
 * right, center and justify.
 * @property {("justify" | "right" | "left" | "center" | "top" | "bottom")} [alignment="justify"]
 * {@attr alignment} Alignment of ui-placement element's children
 *  {@desc justify: makes even space between elements}
 *  {@desc right: push everything to right side only for row direction}
 *  {@desc left: push everything to left side only for row direction}
 *  {@desc center: push everything in center}
 *  {@desc top: push everything to top (only for column direction)}
 *  {@desc bottom: push everything to bottom (only for column direction)}
 * @property {boolean} [spacious] {@attr spacious} Adds some spacing around placements.
 * @property {("row" | "column")} [direction="row"] {@attr direction} Direction or placement.
 *  {@desc row: direction in row}
 *  {@desc column: direction in column}
 * @slot
 * @example
 * <ui-placement alignment="justify">
 *   <h2 class="-left">Hello</h2>
 *   <ui-tooltip class="-right">Helpers text</ui-tooltip>
 * </ui-placement>
 */
@Component({ tag: 'ui-placement', styles })
export class UIPlacement extends UIElement {
    static readonly Alignment: UIPlacementAlignments = {
        Justify: 'justify',
        Right: 'right',
        Left: 'left',
        Center: 'center',
        Top: 'top',
        Bottom: 'bottom',
    } as const;

    static readonly Direction: UIPlacementDirections = {
        Row: 'row',
        Column: 'column',
    } as const;

    @Attribute(String) direction: string = UIPlacement.Direction.Row;
    @Attribute(String) alignment: string = UIPlacement.Alignment.Justify;
    @Attribute(Boolean) spacious?: boolean;
}
